import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldAlert, ChevronDown, ChevronUp, ScrollText, Scale, FileText } from "lucide-react";
import { getLegalTerms } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";

const Section = ({ icon: Icon, title, body, testid, expanded, onToggle }) => (
  <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50"
    >
      <span className="flex items-center gap-2 text-xs font-bold tracking-[0.12em] uppercase text-[#14532D]">
        <Icon className="h-3.5 w-3.5" /> {title}
      </span>
      {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
    </button>
    {expanded && (
      <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-700 leading-relaxed max-h-40 overflow-y-auto whitespace-pre-line">
        {body}
      </div>
    )}
  </div>
);

const LegalConsentModal = ({ open, onAccept }) => {
  const [terms, setTerms] = useState(null);
  const [privacy, setPrivacy] = useState(false);
  const [legal, setLegal] = useState(false);
  const [tos, setTos] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const { t } = useI18n();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    (async () => {
      try {
        const terms = await getLegalTerms();
        if (active) setTerms(terms);
      } catch { /* ignore — gate stays open */ }
    })();
    return () => { active = false; };
  }, [open]);

  const ready = privacy && legal && tos && !!terms;

  const submit = async () => {
    if (!ready || submitting) return;
    setSubmitting(true);
    try {
      await onAccept(terms.version, { privacy, legal, terms: tos });
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        data-testid="legal-consent-modal"
        className="sm:max-w-xl rounded-xl [&>button.absolute]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="h-12 w-12 rounded-xl bg-[#DCFCE7] flex items-center justify-center mb-2">
            <ShieldAlert className="h-6 w-6 text-[#15803D]" />
          </div>
          <DialogTitle className="text-2xl font-semibold tracking-tight text-[#14532D]">
            {t("legal.consent_title")}
          </DialogTitle>
          <DialogDescription className="text-slate-600 pt-1">
            {t("legal.must_read_accept")}
            {terms?.version && (
              <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-mono text-[0.75rem]">
                v{terms.version}
              </span>
            )}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Section
            icon={ScrollText}
            title={t("legal.privacy_policy")}
            testid="legal-section-privacy"
            body={terms?.privacy || t("common.loading")}
            expanded={expanded === "privacy"}
            onToggle={() => setExpanded(expanded === "privacy" ? null : "privacy")}
          />
          <Section
            icon={Scale}
            title={t("legal.legal_notice")}
            testid="legal-section-legal"
            body={terms?.legal_notice || t("common.loading")}
            expanded={expanded === "legal"}
            onToggle={() => setExpanded(expanded === "legal" ? null : "legal")}
          />
          <Section
            icon={FileText}
            title={t("legal.terms_of_use")}
            testid="legal-section-terms"
            body={terms?.terms || t("common.loading")}
            expanded={expanded === "terms"}
            onToggle={() => setExpanded(expanded === "terms" ? null : "terms")}
          />
        </div>

        <div className="space-y-2 mt-2 border-t border-slate-200 pt-3">
          <div
            data-testid="legal-liability-clause"
            className="rounded-lg bg-colygon-light/20 border border-colygon-light px-3 py-2.5 text-xs text-colygon-darkest leading-relaxed"
          >
            <p className="font-semibold mb-1 flex items-center gap-1">
              <ShieldAlert className="h-3.5 w-3.5" />
              {t("liability.title")}
            </p>
            <p>{t("liability.text")}</p>
          </div>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <Checkbox
              data-testid="legal-checkbox-privacy"
              checked={privacy}
              onCheckedChange={(v) => setPrivacy(!!v)}
              className="mt-0.5 data-[state=checked]:bg-[#16A34A] data-[state=checked]:border-[#16A34A]"
            />
            <span className="text-sm text-slate-700">
              {t("legal.accept_privacy_pre")} <strong>{t("legal.privacy_policy")}</strong> {t("legal.accept_privacy_post")}
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <Checkbox
              data-testid="legal-checkbox-legal"
              checked={legal}
              onCheckedChange={(v) => setLegal(!!v)}
              className="mt-0.5 data-[state=checked]:bg-[#16A34A] data-[state=checked]:border-[#16A34A]"
            />
            <span className="text-sm text-slate-700">
              {t("legal.read_accept_legal")}
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <Checkbox
              data-testid="legal-checkbox-terms"
              checked={tos}
              onCheckedChange={(v) => setTos(!!v)}
              className="mt-0.5 data-[state=checked]:bg-[#16A34A] data-[state=checked]:border-[#16A34A]"
            />
            <span className="text-sm text-slate-700">
              {t("legal.accept_terms_pre")} <strong>{t("legal.terms_of_use")}</strong>.
            </span>
          </label>
        </div>

        <button
          onClick={submit}
          disabled={!ready || submitting}
          data-testid="legal-consent-submit"
          className="w-full bg-[#15803D] hover:bg-[#166534] text-white px-5 py-2.5 rounded-xl font-medium transition-colors shadow-sm disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed"
        >
          {submitting ? t("legal.accepting") : t("legal.accept_continue")}
        </button>

        <p className="text-[0.75rem] text-center text-slate-500">
          {terms?.footer_notice || "Colygon-Hub cumple con el RGPD y la LOPDGDD."}
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default LegalConsentModal;
